import { useState, useEffect } from "react";
import { toast } from "sonner";
import { DealStatusControl } from "@/components/DealStatusControl";
import {
  Plus, Trash2, Edit2, Save, X, DollarSign, Calendar, ChevronDown, ChevronRight,
  FileText, TrendingUp, Briefcase, MessageSquare, Loader2, Users,
  AlertTriangle, ListChecks, Clock, XCircle, Archive, GitMerge, RotateCcw,
} from "lucide-react";
import { API, STAGES, ENGAGEMENT_TYPES, CONFIDENCE_LEVELS, CURRENCIES } from "@/lib/constants";
import { CloseOpportunityModal } from "@/components/CloseOpportunityModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const REVENUE_STATUSES = ["Forecast", "Invoiced", "Collected", "Written Off", "Deferred"];
const REVENUE_CATEGORIES = ["Deposit", "Milestone", "Final", "Retainer"];

const STATUS_COLORS = {
  Forecast: "bg-indigo-100 text-indigo-700",
  Invoiced: "bg-amber-100 text-amber-700",
  Collected: "bg-teal-100 text-teal-700",
  "Written Off": "bg-red-100 text-red-700",
  Deferred: "bg-slate-100 text-slate-600",
};

function fmtCurrency(val, currency = "NGN") {
  if (!val || val === 0) return "\u20A60";
  const sym = currency === "USD" ? "$" : currency === "GBP" ? "\u00A3" : currency === "EUR" ? "\u20AC" : "\u20A6";
  if (val >= 1_000_000_000) return `${sym}${(val / 1_000_000_000).toFixed(1)}B`;
  if (val >= 1_000_000) return `${sym}${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `${sym}${(val / 1_000).toFixed(0)}K`;
  return `${sym}${val.toLocaleString()}`;
}


function OppNotes({ opportunityId, canEdit }) {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editContent, setEditContent] = useState("");

  useEffect(() => {
    fetchNotes();
  }, [opportunityId]);

  const fetchNotes = async () => {
    try {
      const res = await fetch(`${API}/notes?parent_id=${opportunityId}&parent_type=opportunity`, { credentials: "include" });
      if (res.ok) setNotes(await res.json());
    } catch { /* ignore */ }
  };

  const handleAdd = async () => {
    if (!newNote.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ parent_id: opportunityId, parent_type: "opportunity", content: newNote.trim() }),
      });
      if (res.ok) { setNewNote(""); fetchNotes(); }
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const handleUpdate = async (noteId) => {
    try {
      await fetch(`${API}/notes/${noteId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: editContent.trim() }),
      });
      setEditId(null);
      fetchNotes();
    } catch { /* ignore */ }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await fetch(`${API}/notes/${noteId}`, { method: "DELETE", credentials: "include" });
      fetchNotes();
    } catch { /* ignore */ }
  };

  return (
    <div data-testid={`opp-notes-${opportunityId}`}>
      <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-2">
        <MessageSquare className="w-4 h-4" /> Notes ({notes.length})
      </h4>

      {canEdit && (
        <div className="flex gap-2 mb-2">
          <Input
            placeholder="Add a note..."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            className="text-sm h-8"
            data-testid={`opp-note-input-${opportunityId}`}
          />
          <Button size="sm" className="h-8 shrink-0" onClick={handleAdd} disabled={saving || !newNote.trim()} data-testid={`opp-note-add-${opportunityId}`}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
          </Button>
        </div>
      )}

      {notes.length > 0 && (
        <div className="space-y-1.5 max-h-48 overflow-y-auto">
          {notes.map((n) => (
            <div key={n.note_id} className="bg-slate-50 rounded px-2.5 py-1.5 text-xs group" data-testid={`opp-note-${n.note_id}`}>
              {editId === n.note_id ? (
                <div className="flex gap-1">
                  <Input value={editContent} onChange={(e) => setEditContent(e.target.value)} className="h-6 text-xs" />
                  <Button size="sm" className="h-6 text-[10px] px-2" onClick={() => handleUpdate(n.note_id)}>Save</Button>
                  <Button size="sm" variant="ghost" className="h-6 text-[10px] px-1" onClick={() => setEditId(null)}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-700">{n.content}</p>
                    <span className="text-slate-400 text-[10px]">
                      {n.author_name} &middot; {new Date(n.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                  {canEdit && (
                    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      <button className="p-0.5 hover:text-blue-600" onClick={() => { setEditId(n.note_id); setEditContent(n.content); }}>
                        <Edit2 className="w-3 h-3" />
                      </button>
                      <button className="p-0.5 hover:text-red-600" onClick={() => handleDelete(n.note_id)}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function OpportunitiesSection({
  opportunities, clientId, canEdit, users, user, onRefresh,
  showAddOpp, setShowAddOpp, newOpp, setNewOpp,
  editingOppId, setEditingOppId, editOppData, setEditOppData,
  showAddRevEntry, setShowAddRevEntry, newRevEntry, setNewRevEntry,
  focusOppId,
}) {
  const [expandedOpp, setExpandedOpp] = useState(null);
  const [flashOppId, setFlashOppId] = useState(null);   // deal arrived at from the revenue correction list
  const [saving, setSaving] = useState(false);
  const [closingOpp, setClosingOpp] = useState(null);
  const [mergeSourceId, setMergeSourceId] = useState(null);   // the loser chosen for a merge
  const [showArchived, setShowArchived] = useState(false);
  const [archivedOpps, setArchivedOpps] = useState([]);
  const isAdmin = user?.role === "admin";

  // When opened from the revenue-tab correction list, identify the exact deal: expand it,
  // scroll it into view and flash a highlight so the person fixes it without hunting.
  useEffect(() => {
    if (!focusOppId || !opportunities?.some((o) => o.opportunity_id === focusOppId)) return;
    setExpandedOpp(focusOppId);
    setFlashOppId(focusOppId);
    const t1 = setTimeout(() => {
      const el = document.querySelector(`[data-testid="opp-${focusOppId}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 250);
    const t2 = setTimeout(() => setFlashOppId(null), 3200);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [focusOppId, opportunities]);

  const fetchArchived = async () => {
    try {
      const res = await fetch(`${API}/opportunities?client_id=${clientId}&archived=true`, { credentials: "include" });
      if (res.ok) setArchivedOpps(await res.json());
    } catch { /* ignore */ }
  };
  useEffect(() => { if (showArchived) fetchArchived(); }, [showArchived]);

  const handleArchiveOpp = async (oppId) => {
    const reason = window.prompt("Archive this deal — it leaves the ladder and the money figures but stays findable and restorable.\n\nReason (what is the outcome?):", "");
    if (reason === null) return;  // cancelled
    try {
      const res = await fetch(`${API}/opportunities/${oppId}/archive`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ reason: reason.trim(), outcome: reason.trim() }),
      });
      if (res.ok) { toast.success("Archived — still findable under Archived"); onRefresh(); }
      else toast.error("Failed to archive");
    } catch { toast.error("Network error"); }
  };

  const handleRestoreOpp = async (oppId) => {
    try {
      const res = await fetch(`${API}/opportunities/${oppId}/restore`, { method: "POST", credentials: "include" });
      if (res.ok) { toast.success("Restored to live"); fetchArchived(); onRefresh(); }
      else toast.error((await res.json()).detail || "Failed to restore");
    } catch { toast.error("Network error"); }
  };

  const handleMerge = async (survivorId) => {
    if (!mergeSourceId || mergeSourceId === survivorId) { setMergeSourceId(null); return; }
    const loser = opportunities.find(o => o.opportunity_id === mergeSourceId);
    const survivor = opportunities.find(o => o.opportunity_id === survivorId);
    if (!window.confirm(`Merge "${loser?.opportunity_name}" INTO "${survivor?.opportunity_name}"?\n\nThe loser's notes, payments and revenue entries move onto the survivor, and the loser is archived pointing at it. It is never deleted.`)) return;
    try {
      const res = await fetch(`${API}/opportunities/merge`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ survivor_id: survivorId, loser_id: mergeSourceId, reason: "true duplicate" }),
      });
      if (res.ok) { toast.success("Merged"); setMergeSourceId(null); onRefresh(); }
      else toast.error("Failed to merge");
    } catch { toast.error("Network error"); }
  };

  const handleHardDelete = async (oppId, name) => {
    const reason = window.prompt(`HARD DELETE "${name}" — permanent, admin only, blocked if it carries any payment or revenue entry.\n\nReason (required):`, "");
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    try {
      const res = await fetch(`${API}/opportunities/${oppId}/hard-delete`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ reason: reason.trim() }),
      });
      if (res.ok) { toast.success("Permanently deleted"); onRefresh(); }
      else toast.error((await res.json()).detail || "Failed to delete");
    } catch { toast.error("Network error"); }
  };

  const handleCreateOpp = async () => {
    if (!newOpp.opportunity_name.trim()) {
      toast.error("Opportunity name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        client_id: clientId,
        opportunity_name: newOpp.opportunity_name,
        engagement_type: newOpp.engagement_type,
        stage: newOpp.stage,
        opportunity_value: newOpp.opportunity_value ? parseFloat(newOpp.opportunity_value) : null,
        opportunity_currency: newOpp.opportunity_currency,
        expected_close_date: newOpp.expected_close_date || null,
        deal_owner_id: newOpp.deal_owner_id || user?.user_id,
        deal_owner_name: newOpp.deal_owner_name || user?.name,
        account_manager_id: newOpp.account_manager_id || null,
        account_manager_name: newOpp.account_manager_name || null,
      };
      const res = await fetch(`${API}/opportunities`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success("Opportunity created");
        setShowAddOpp(false);
        setNewOpp({ opportunity_name: "", engagement_type: "Commercial Opportunity", stage: "Unqualified Leads", opportunity_value: "", opportunity_currency: "NGN", expected_close_date: "", deal_owner_id: "", deal_owner_name: "", account_manager_id: "", account_manager_name: "" });
        onRefresh();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create opportunity");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateOpp = async (oppId) => {
    setSaving(true);
    try {
      const payload = { ...editOppData };
      if (payload.opportunity_value) payload.opportunity_value = parseFloat(payload.opportunity_value);
      if (payload.contract_value) payload.contract_value = parseFloat(payload.contract_value);
      const res = await fetch(`${API}/opportunities/${oppId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success("Opportunity updated");
        setEditingOppId(null);
        onRefresh();
      } else {
        toast.error("Failed to update");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };

  const handleAddRevEntry = async (oppId) => {
    if (!newRevEntry.amount || !newRevEntry.scheduled_date) {
      toast.error("Amount and date are required");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/opportunities/${oppId}/revenue-entries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...newRevEntry,
          amount: parseFloat(newRevEntry.amount),
        }),
      });
      if (res.ok) {
        toast.success("Revenue entry added");
        setShowAddRevEntry(null);
        setNewRevEntry({ amount: "", currency: "NGN", scheduled_date: "", status: "Forecast", category: "Milestone", description: "" });
        onRefresh();
      } else {
        toast.error("Failed to add entry");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRevEntry = async (oppId, entryId, updates) => {
    try {
      const res = await fetch(`${API}/opportunities/${oppId}/revenue-entries/${entryId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updates),
      });
      if (res.ok) {
        toast.success("Entry updated");
        onRefresh();
      }
    } catch {
      toast.error("Network error");
    }
  };

  const handleDeleteRevEntry = async (oppId, entryId) => {
    try {
      const res = await fetch(`${API}/opportunities/${oppId}/revenue-entries/${entryId}`, { method: "DELETE", credentials: "include" });
      if (res.ok) {
        toast.success("Entry removed");
        onRefresh();
      }
    } catch {
      toast.error("Network error");
    }
  };

  const handleCreateTaskFromMilestone = async (oppId, entryId) => {
    try {
      const res = await fetch(`${API}/opportunities/${oppId}/revenue-entries/${entryId}/create-task`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (res.ok) {
        const task = await res.json();
        toast.success(`Scrum task created: ${task.title?.slice(0, 60)}...`);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to create task");
      }
    } catch {
      toast.error("Network error");
    }
  };

  // Compute totals
  const now = new Date();
  const totalValue = opportunities.reduce((sum, o) => sum + (o.opportunity_value || 0), 0);
  const totalCollected = opportunities.reduce((sum, o) => {
    return sum + (o.revenue_entries || []).filter(e => e.status === "Collected").reduce((s, e) => s + e.amount, 0);
  }, 0);
  const totalInvoiced = opportunities.reduce((sum, o) => {
    return sum + (o.revenue_entries || []).filter(e => e.status === "Invoiced").reduce((s, e) => s + e.amount, 0);
  }, 0);
  const totalForecast = opportunities.reduce((sum, o) => {
    return sum + (o.revenue_entries || []).filter(e => e.status === "Forecast").reduce((s, e) => s + e.amount, 0);
  }, 0);
  const totalOverdue = opportunities.reduce((sum, o) => {
    return sum + (o.revenue_entries || []).filter(e => {
      if (!e.scheduled_date || (e.status !== "Forecast" && e.status !== "Invoiced")) return false;
      return new Date(e.scheduled_date) < now;
    }).reduce((s, e) => s + e.amount, 0);
  }, 0);
  const overdueCount = opportunities.reduce((count, o) => {
    return count + (o.revenue_entries || []).filter(e => {
      if (!e.scheduled_date || (e.status !== "Forecast" && e.status !== "Invoiced")) return false;
      return new Date(e.scheduled_date) < now;
    }).length;
  }, 0);
  const totalRevEntries = opportunities.reduce((s, o) => s + (o.revenue_entries || []).length, 0);
  const collectionRate = (totalCollected + totalInvoiced + totalForecast) > 0
    ? Math.round((totalCollected / (totalCollected + totalInvoiced + totalForecast)) * 100)
    : 0;

  return (
    <Card data-testid="opportunities-section">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div>
          <CardTitle className="text-lg">Opportunities ({opportunities.length})</CardTitle>
          <p className="text-xs text-slate-400 mt-0.5">{totalRevEntries} payment milestones tracked</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setShowArchived(s => !s)} data-testid="toggle-archived-btn">
              <Archive className="w-3.5 h-3.5 mr-1" /> {showArchived ? "Hide archived" : "Archived"}
            </Button>
            <Button size="sm" onClick={() => setShowAddOpp(!showAddOpp)} data-testid="add-opportunity-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Opportunity
            </Button>
          </div>
        )}
      </CardHeader>

      {/* Milestone Summary Cards */}
      {totalRevEntries > 0 && (
        <div className="px-6 pb-4" data-testid="milestone-summary-cards">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Total Value</p>
              <p className="text-lg font-bold text-slate-800 mt-0.5" data-testid="summary-total-value">{fmtCurrency(totalValue)}</p>
            </div>
            <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
              <p className="text-[10px] uppercase tracking-wider text-indigo-400 font-medium">Forecast</p>
              <p className="text-lg font-bold text-indigo-700 mt-0.5" data-testid="summary-forecast">{fmtCurrency(totalForecast)}</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
              <p className="text-[10px] uppercase tracking-wider text-amber-400 font-medium">Invoiced</p>
              <p className="text-lg font-bold text-amber-700 mt-0.5" data-testid="summary-invoiced">{fmtCurrency(totalInvoiced)}</p>
            </div>
            <div className="bg-teal-50 rounded-lg p-3 border border-teal-100">
              <p className="text-[10px] uppercase tracking-wider text-teal-400 font-medium">Collected</p>
              <p className="text-lg font-bold text-teal-700 mt-0.5" data-testid="summary-collected">{fmtCurrency(totalCollected)}</p>
              <p className="text-[10px] text-teal-500 mt-0.5">{collectionRate}% rate</p>
            </div>
            {overdueCount > 0 && (
              <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                <p className="text-[10px] uppercase tracking-wider text-red-400 font-medium flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Overdue
                </p>
                <p className="text-lg font-bold text-red-700 mt-0.5" data-testid="summary-overdue">{fmtCurrency(totalOverdue)}</p>
                <p className="text-[10px] text-red-500 mt-0.5">{overdueCount} entries</p>
              </div>
            )}
          </div>

          {/* Overall Collection Progress Bar */}
          <div className="mt-3" data-testid="overall-progress-bar">
            <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
              <span>Payment Progress</span>
              <span>{fmtCurrency(totalCollected)} / {fmtCurrency(totalCollected + totalInvoiced + totalForecast)}</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden flex">
              {totalCollected > 0 && (
                <div
                  className="h-full bg-teal-500 transition-all duration-700"
                  style={{ width: `${(totalCollected / (totalCollected + totalInvoiced + totalForecast)) * 100}%` }}
                  title={`Collected: ${fmtCurrency(totalCollected)}`}
                />
              )}
              {totalInvoiced > 0 && (
                <div
                  className="h-full bg-amber-400 transition-all duration-700"
                  style={{ width: `${(totalInvoiced / (totalCollected + totalInvoiced + totalForecast)) * 100}%` }}
                  title={`Invoiced: ${fmtCurrency(totalInvoiced)}`}
                />
              )}
            </div>
            <div className="flex gap-4 mt-1 text-[10px] text-slate-400">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-teal-500 inline-block" /> Collected</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" /> Invoiced</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-100 inline-block" /> Forecast</span>
            </div>
          </div>
        </div>
      )}
      <CardContent className="space-y-3">
        {mergeSourceId && (
          <div className="rounded-md border border-violet-300 bg-violet-50 p-2 text-xs text-violet-800 flex items-center justify-between" data-testid="merge-hint">
            <span><GitMerge className="w-3.5 h-3.5 inline mr-1" />Merging <b>{opportunities.find(o => o.opportunity_id === mergeSourceId)?.opportunity_name}</b> — now click “merge into this” on the deal that should survive.</span>
            <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => setMergeSourceId(null)}>Cancel</Button>
          </div>
        )}
        {showArchived && (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-2" data-testid="archived-list">
            <p className="text-xs font-semibold text-slate-600 mb-1">Archived deals ({archivedOpps.length}) — kept with full history, restorable</p>
            {archivedOpps.length === 0 ? (
              <p className="text-[11px] text-slate-400">None archived.</p>
            ) : archivedOpps.map(a => (
              <div key={a.opportunity_id} className="flex items-center justify-between py-1 border-t border-slate-100 text-xs" data-testid={`archived-${a.opportunity_id}`}>
                <span className="min-w-0 truncate">
                  <span className="font-medium">{a.opportunity_name}</span>
                  <span className="text-slate-400"> · {a.stage}{a.merged_into ? " · merged" : a.archive_reason ? ` · ${a.archive_reason}` : ""}</span>
                </span>
                {canEdit && !a.merged_into && (
                  <Button size="sm" variant="ghost" className="h-6 text-[11px] text-emerald-600" data-testid={`restore-${a.opportunity_id}`} onClick={() => handleRestoreOpp(a.opportunity_id)}>
                    <RotateCcw className="w-3 h-3 mr-1" /> Restore
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
        {/* Add Opportunity Form */}
        {showAddOpp && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3" data-testid="add-opportunity-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Opportunity Name *</Label>
                <Input
                  placeholder="e.g. Leadership Programme"
                  value={newOpp.opportunity_name}
                  onChange={(e) => setNewOpp({ ...newOpp, opportunity_name: e.target.value })}
                  data-testid="new-opp-name"
                />
              </div>
              <div>
                <Label className="text-xs">Engagement Type</Label>
                <Select value={newOpp.engagement_type} onValueChange={(v) => setNewOpp({ ...newOpp, engagement_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ENGAGEMENT_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Stage</Label>
                <Select value={newOpp.stage} onValueChange={(v) => setNewOpp({ ...newOpp, stage: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STAGES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Opportunity Value</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={newOpp.opportunity_value}
                  onChange={(e) => setNewOpp({ ...newOpp, opportunity_value: e.target.value })}
                  data-testid="new-opp-value"
                />
              </div>
              <div>
                <Label className="text-xs">Expected Close Date</Label>
                <Input
                  type="date"
                  value={newOpp.expected_close_date}
                  onChange={(e) => setNewOpp({ ...newOpp, expected_close_date: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Deal Owner</Label>
                <Select value={newOpp.deal_owner_id} onValueChange={(v) => {
                  const u = users.find(x => x.user_id === v);
                  setNewOpp({ ...newOpp, deal_owner_id: v, deal_owner_name: u?.name || "" });
                }}>
                  <SelectTrigger><SelectValue placeholder="Select owner" /></SelectTrigger>
                  <SelectContent>
                    {users.filter(u => u.role !== "viewer" && u.is_active !== false).map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Account Manager</Label>
                <Select value={newOpp.account_manager_id || ""} onValueChange={(v) => {
                  const u = users.find(x => x.user_id === v);
                  setNewOpp({ ...newOpp, account_manager_id: v, account_manager_name: u?.name || "" });
                }}>
                  <SelectTrigger><SelectValue placeholder="Select manager" /></SelectTrigger>
                  <SelectContent>
                    {users.filter(u => u.role !== "viewer" && u.is_active !== false).map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleCreateOpp} disabled={saving} data-testid="save-opportunity-btn">
                {saving ? "Saving..." : "Create Opportunity"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowAddOpp(false)}>Cancel</Button>
            </div>
          </div>
        )}

        {/* Opportunities List */}
        {opportunities.length === 0 && !showAddOpp && (
          <p className="text-slate-400 text-center py-6 text-sm">No opportunities yet. Click "Add Opportunity" to create one.</p>
        )}

        {opportunities.map((opp) => {
          const isExpanded = expandedOpp === opp.opportunity_id;
          const isEditing = editingOppId === opp.opportunity_id;
          const revEntries = opp.revenue_entries || [];
          const oppCollected = revEntries.filter(e => e.status === "Collected").reduce((s, e) => s + e.amount, 0);
          const oppInvoiced = revEntries.filter(e => e.status === "Invoiced").reduce((s, e) => s + e.amount, 0);
          const oppForecast = revEntries.filter(e => e.status === "Forecast").reduce((s, e) => s + e.amount, 0);
          const oppTotal = oppCollected + oppInvoiced + oppForecast;
          const oppOverdueEntries = revEntries.filter(e => {
            if (!e.scheduled_date || (e.status !== "Forecast" && e.status !== "Invoiced")) return false;
            return new Date(e.scheduled_date) < now;
          });
          const oppOverdueAmt = oppOverdueEntries.reduce((s, e) => s + e.amount, 0);
          const oppProgress = oppTotal > 0 ? Math.round((oppCollected / oppTotal) * 100) : 0;
          const stageObj = STAGES.find(s => s.id === opp.stage);

          return (
            <div key={opp.opportunity_id} className={`border rounded-lg overflow-hidden transition-all duration-500 ${flashOppId === opp.opportunity_id ? "border-rose-400 ring-2 ring-rose-300 shadow-md" : "border-slate-200"}`} data-testid={`opp-${opp.opportunity_id}`}>
              {/* Header */}
              <div
                className="flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors"
                onClick={() => setExpandedOpp(isExpanded ? null : opp.opportunity_id)}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 text-sm truncate">{opp.opportunity_name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${stageObj?.color?.replace("bg-", "border-")} ${stageObj?.color?.replace("bg-", "text-")}`}>
                        {opp.stage}
                      </Badge>
                      <span className="text-xs text-slate-400">{opp.engagement_type}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-right shrink-0">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{fmtCurrency(opp.opportunity_value, opp.opportunity_currency)}</p>
                    {revEntries.length > 0 && (
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden flex" data-testid={`opp-progress-${opp.opportunity_id}`}>
                          {oppCollected > 0 && <div className="h-full bg-teal-500" style={{ width: `${oppTotal > 0 ? (oppCollected / oppTotal) * 100 : 0}%` }} />}
                          {oppInvoiced > 0 && <div className="h-full bg-amber-400" style={{ width: `${oppTotal > 0 ? (oppInvoiced / oppTotal) * 100 : 0}%` }} />}
                        </div>
                        <span className="text-[10px] text-slate-400">{oppProgress}%</span>
                      </div>
                    )}
                    {oppOverdueEntries.length > 0 && (
                      <span className="text-[10px] text-red-500 flex items-center gap-0.5 justify-end mt-0.5" data-testid={`opp-overdue-${opp.opportunity_id}`}>
                        <AlertTriangle className="w-3 h-3" /> {oppOverdueEntries.length} overdue
                      </span>
                    )}
                  </div>
                  {canEdit && (
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      {mergeSourceId && mergeSourceId !== opp.opportunity_id ? (
                        <Button size="sm" variant="outline" className="h-7 text-[10px] px-1.5 border-violet-400 text-violet-700" data-testid={`opp-merge-into-${opp.opportunity_id}`} onClick={() => handleMerge(opp.opportunity_id)}>
                          merge into this
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost" className={`h-7 w-7 p-0 ${mergeSourceId === opp.opportunity_id ? "text-violet-700 bg-violet-100" : "text-slate-400 hover:text-violet-600"}`} title={mergeSourceId === opp.opportunity_id ? "Pick the surviving deal (click the other row's button)" : "Merge this duplicate into another deal"} data-testid={`opp-merge-${opp.opportunity_id}`} onClick={() => setMergeSourceId(mergeSourceId === opp.opportunity_id ? null : opp.opportunity_id)}>
                          <GitMerge className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid={`opp-edit-${opp.opportunity_id}`} onClick={() => {
                        setEditingOppId(opp.opportunity_id);
                        setEditOppData({
                          opportunity_name: opp.opportunity_name,
                          stage: opp.stage,
                          opportunity_value: opp.opportunity_value || "",
                          opportunity_currency: opp.opportunity_currency || "NGN",
                          contract_value: opp.contract_value || "",
                          contract_start_date: opp.contract_start_date || "",
                          contract_end_date: opp.contract_end_date || "",
                          expected_close_date: opp.expected_close_date || "",
                          actual_close_date: opp.actual_close_date || "",
                          confidence_level: opp.confidence_level || "",
                          deal_owner_id: opp.deal_owner_id || "",
                          deal_owner_name: opp.deal_owner_name || "",
                          account_manager_id: opp.account_manager_id || "",
                          account_manager_name: opp.account_manager_name || "",
                          notes: opp.notes || "",
                        });
                        setExpandedOpp(opp.opportunity_id);
                      }}>
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-500 hover:text-slate-800" title="Close deal (four endings)" data-testid={`opp-close-${opp.opportunity_id}`} onClick={() => setClosingOpp(opp)}>
                        <XCircle className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-amber-500 hover:text-amber-700" title="Archive (reversible, keeps history)" data-testid={`opp-archive-${opp.opportunity_id}`} onClick={() => handleArchiveOpp(opp.opportunity_id)}>
                        <Archive className="w-3.5 h-3.5" />
                      </Button>
                      {isAdmin && (
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" title="Hard delete (admin only, reserved)" data-testid={`opp-harddelete-${opp.opportunity_id}`} onClick={() => handleHardDelete(opp.opportunity_id, opp.opportunity_name)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="p-4 border-t border-slate-200 space-y-4">
                  {/* Opportunity Details (view or edit) */}
                  {isEditing ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs">Name</Label>
                          <Input value={editOppData.opportunity_name || ""} onChange={(e) => setEditOppData({ ...editOppData, opportunity_name: e.target.value })} />
                        </div>
                        <div>
                          <Label className="text-xs">Stage</Label>
                          <p className="text-xs text-slate-600" data-testid="opp-edit-stage-readonly">{editOppData.stage || "—"}</p>
                          <p className="text-[10px] text-slate-400">Use the status control below to change stage.</p>
                        </div>
                        <div>
                          <Label className="text-xs">Opportunity Value</Label>
                          <Input type="number" value={editOppData.opportunity_value || ""} onChange={(e) => setEditOppData({ ...editOppData, opportunity_value: e.target.value })} data-testid="opp-edit-value" />
                        </div>
                        <div>
                          <Label className="text-xs">Currency</Label>
                          <Select value={editOppData.opportunity_currency || "NGN"} onValueChange={(v) => setEditOppData({ ...editOppData, opportunity_currency: v })}>
                            <SelectTrigger data-testid="opp-edit-currency"><SelectValue /></SelectTrigger>
                            <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Contract Value</Label>
                          <Input type="number" value={editOppData.contract_value || ""} onChange={(e) => setEditOppData({ ...editOppData, contract_value: e.target.value })} />
                        </div>
                        <div>
                          <Label className="text-xs">Contract Start</Label>
                          <Input type="date" value={editOppData.contract_start_date || ""} onChange={(e) => setEditOppData({ ...editOppData, contract_start_date: e.target.value })} />
                        </div>
                        <div>
                          <Label className="text-xs">Contract End</Label>
                          <Input type="date" value={editOppData.contract_end_date || ""} onChange={(e) => setEditOppData({ ...editOppData, contract_end_date: e.target.value })} />
                        </div>
                        <div>
                          <Label className="text-xs">Expected Close</Label>
                          <Input type="date" value={editOppData.expected_close_date || ""} onChange={(e) => setEditOppData({ ...editOppData, expected_close_date: e.target.value })} />
                        </div>
                        <div>
                          <Label className="text-xs">Actual Close</Label>
                          <Input type="date" value={editOppData.actual_close_date || ""} onChange={(e) => setEditOppData({ ...editOppData, actual_close_date: e.target.value })} />
                        </div>
                        <div>
                          <Label className="text-xs">Confidence</Label>
                          <Select value={editOppData.confidence_level || ""} onValueChange={(v) => setEditOppData({ ...editOppData, confidence_level: v })}>
                            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>{CONFIDENCE_LEVELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Deal Owner</Label>
                          <Select value={editOppData.deal_owner_id || ""} onValueChange={(v) => {
                            const u = users.find(x => x.user_id === v);
                            setEditOppData({ ...editOppData, deal_owner_id: v, deal_owner_name: u?.name || "" });
                          }}>
                            <SelectTrigger><SelectValue placeholder="Select owner" /></SelectTrigger>
                            <SelectContent>{users.filter(u => u.role !== "viewer" && u.is_active !== false).map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Account Manager</Label>
                          <Select value={editOppData.account_manager_id || ""} onValueChange={(v) => {
                            const u = users.find(x => x.user_id === v);
                            setEditOppData({ ...editOppData, account_manager_id: v, account_manager_name: u?.name || "" });
                          }}>
                            <SelectTrigger><SelectValue placeholder="Select manager" /></SelectTrigger>
                            <SelectContent>{users.filter(u => u.role !== "viewer" && u.is_active !== false).map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleUpdateOpp(opp.opportunity_id)} disabled={saving}>
                          <Save className="w-3.5 h-3.5 mr-1" /> Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingOppId(null)}>
                          <X className="w-3.5 h-3.5 mr-1" /> Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                      <div><span className="text-slate-400">Deal Owner</span><p className="font-medium">{opp.deal_owner_name || "-"}</p></div>
                      <div><span className="text-slate-400">Account Manager</span><p className="font-medium">{opp.account_manager_name || "-"}</p></div>
                      <div><span className="text-slate-400">Confidence</span><p className="font-medium">{opp.confidence_level || "-"}</p></div>
                      <div><span className="text-slate-400">Expected Close</span><p className="font-medium">{opp.expected_close_date || "-"}</p></div>
                      <div><span className="text-slate-400">Actual Close</span><p className="font-medium">{opp.actual_close_date || "-"}</p></div>
                      {opp.contract_value && <div><span className="text-slate-400">Contract Value</span><p className="font-medium">{fmtCurrency(opp.contract_value)}</p></div>}
                      {opp.contract_start_date && <div><span className="text-slate-400">Contract Start</span><p className="font-medium">{opp.contract_start_date}</p></div>}
                      {opp.contract_end_date && <div><span className="text-slate-400">Contract End</span><p className="font-medium">{opp.contract_end_date}</p></div>}
                      {opp.notes && <div className="col-span-2 sm:col-span-4"><span className="text-slate-400">Notes</span><p className="font-medium whitespace-pre-wrap">{opp.notes}</p></div>}
                    </div>
                  )}

                  {/* Opportunity Notes */}
                  <OppNotes opportunityId={opp.opportunity_id} canEdit={canEdit} />

                  {/* L1 — one shared status control */}
                  <div className="rounded-md border border-slate-200 bg-slate-50 p-2">
                    <DealStatusControl dealId={opp.opportunity_id} currentStage={opp.stage} canAct={canEdit}
                      testidPrefix={`opp-status-${opp.opportunity_id}`} onChanged={() => onRefresh && onRefresh()} />
                  </div>

                  {/* Revenue Schedule */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                        <DollarSign className="w-4 h-4" /> Revenue Schedule ({revEntries.length})
                      </h4>
                      {canEdit && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowAddRevEntry(showAddRevEntry === opp.opportunity_id ? null : opp.opportunity_id)} data-testid="add-rev-entry-btn">
                          <Plus className="w-3 h-3 mr-1" /> Add Entry
                        </Button>
                      )}
                    </div>

                    {/* Summary bar */}
                    {revEntries.length > 0 && (
                      <div className="flex gap-3 mb-2 text-[11px]">
                        <span className="text-teal-600">Collected: <strong>{fmtCurrency(oppCollected)}</strong></span>
                        <span className="text-amber-600">Invoiced: <strong>{fmtCurrency(oppInvoiced)}</strong></span>
                        <span className="text-indigo-600">Forecast: <strong>{fmtCurrency(oppForecast)}</strong></span>
                      </div>
                    )}

                    {/* Add Entry Form */}
                    {showAddRevEntry === opp.opportunity_id && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-2 space-y-2" data-testid="add-rev-entry-form">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          <div>
                            <Label className="text-xs">Amount *</Label>
                            <Input type="number" placeholder="0" value={newRevEntry.amount} onChange={(e) => setNewRevEntry({ ...newRevEntry, amount: e.target.value })} data-testid="rev-entry-amount" />
                          </div>
                          <div>
                            <Label className="text-xs">Date *</Label>
                            <Input type="date" value={newRevEntry.scheduled_date} onChange={(e) => setNewRevEntry({ ...newRevEntry, scheduled_date: e.target.value })} data-testid="rev-entry-date" />
                          </div>
                          <div>
                            <Label className="text-xs">Status</Label>
                            <Select value={newRevEntry.status} onValueChange={(v) => setNewRevEntry({ ...newRevEntry, status: v })}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>{REVENUE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Category</Label>
                            <Select value={newRevEntry.category} onValueChange={(v) => setNewRevEntry({ ...newRevEntry, category: v })}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>{REVENUE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs">Description</Label>
                            <Input placeholder="e.g. Phase 1 delivery milestone" value={newRevEntry.description} onChange={(e) => setNewRevEntry({ ...newRevEntry, description: e.target.value })} />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleAddRevEntry(opp.opportunity_id)} disabled={saving} data-testid="save-rev-entry-btn">
                            {saving ? "Saving..." : "Add Entry"}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setShowAddRevEntry(null)}>Cancel</Button>
                        </div>
                      </div>
                    )}

                    {/* Revenue Entries Table */}
                    {revEntries.length > 0 ? (
                      <div className="border border-slate-200 rounded-lg overflow-hidden">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="text-left p-2 font-medium text-slate-500">Date</th>
                              <th className="text-left p-2 font-medium text-slate-500">Description</th>
                              <th className="text-left p-2 font-medium text-slate-500">Category</th>
                              <th className="text-right p-2 font-medium text-slate-500">Amount</th>
                              <th className="text-center p-2 font-medium text-slate-500">Status</th>
                              {canEdit && <th className="text-center p-2 font-medium text-slate-500 w-24">Actions</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {revEntries.sort((a, b) => (a.scheduled_date || "").localeCompare(b.scheduled_date || "")).map((entry) => {
                              const entryOverdue = entry.scheduled_date && (entry.status === "Forecast" || entry.status === "Invoiced") && new Date(entry.scheduled_date) < now;
                              return (
                              <tr key={entry.entry_id} className={`border-t border-slate-100 hover:bg-slate-50 ${entryOverdue ? "bg-red-50/40" : ""}`} data-testid={`rev-entry-${entry.entry_id}`}>
                                <td className="p-2 text-slate-700">
                                  {entry.scheduled_date}
                                  {entryOverdue && <AlertTriangle className="w-3 h-3 text-red-500 inline ml-1" />}
                                </td>
                                <td className="p-2 text-slate-600">{entry.description || "-"}</td>
                                <td className="p-2"><Badge variant="outline" className="text-[10px]">{entry.category}</Badge></td>
                                <td className="p-2 text-right font-medium text-slate-900">{fmtCurrency(entry.amount, entry.currency)}</td>
                                <td className="p-2 text-center">
                                  {canEdit ? (
                                    <Select value={entry.status} onValueChange={(v) => handleUpdateRevEntry(opp.opportunity_id, entry.entry_id, { status: v })}>
                                      <SelectTrigger className="h-6 text-[10px] w-24 mx-auto">
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_COLORS[entry.status] || ""}`}>{entry.status}</span>
                                      </SelectTrigger>
                                      <SelectContent>{REVENUE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                    </Select>
                                  ) : (
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_COLORS[entry.status] || ""}`}>{entry.status}</span>
                                  )}
                                </td>
                                {canEdit && (
                                  <td className="p-2 text-center">
                                    <div className="flex items-center justify-center gap-0.5">
                                      <Button
                                        size="sm" variant="ghost" className="h-6 w-6 p-0 text-blue-400 hover:text-blue-600"
                                        title="Create Scrum task from milestone"
                                        onClick={() => handleCreateTaskFromMilestone(opp.opportunity_id, entry.entry_id)}
                                        data-testid={`create-task-${entry.entry_id}`}
                                      >
                                        <ListChecks className="w-3 h-3" />
                                      </Button>
                                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={() => handleDeleteRevEntry(opp.opportunity_id, entry.entry_id)}>
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </td>
                                )}
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-slate-400 text-xs text-center py-3">No revenue entries. Add payment milestones to track cash flow.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
      {closingOpp && (
        <CloseOpportunityModal
          opportunity={closingOpp}
          open={!!closingOpp}
          onClose={() => setClosingOpp(null)}
          onClosed={() => { setClosingOpp(null); onRefresh && onRefresh(); }}
        />
      )}
    </Card>
  );
}
